import React, { ReactElement, useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Table, Button, Modal,Popover } from 'antd'
import { useTranslation } from 'react-i18next'
import type { IProjectInfoResData } from '@/store/models/tableEdit.model'
import { viewProjectHistory,getProjectInfoById } from '@/services/table.service'
import { getErrorStr } from '@/store/sagas/table.saga'
import { requestFail } from '@/store/actions/common.action'
import { getUuid } from '@/utils/util'
import moment from 'moment'

import * as go from 'gojs'
import { ReactDiagram } from 'gojs-react'

import './gojs.css' 

export default function HistoryListPop ( { recordId, setViewHistoryId }: { 
    recordId: number | null, setViewHistoryId: any
}): ReactElement {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [logLoading, setLogLoading] = useState(false)
    const [tableKey, setTableKey] = useState('a')
    const [curLogList, setCurLogList] = useState<IProjectInfoResData[]>([])
    const [nodeDataArray, setNodeDataArray] = useState<{ key: number; question: string; actions: { text: string; figure: string; fill: string; }[]; }[]>([])
    const [linkDataArray, setLinkDataArray] = useState<{ from: number; to: number; }[]>([])
    const [record, setRecord] = useState<IProjectInfoResData>()
    const [winH, setWinh] = useState<number>(0)
    const [paginationInfo, setPaginationInfo] = useState<{pageIndex:number,pageSize:number,total:number}>({ pageIndex:1,pageSize:4,total:0 })

    const viewLogList = async (id: number,pageNo:number) => {
        setIsModalOpen(true)
        setLogLoading(true)
        try {
            const { content,number,size,totalElements }: 
                { content: IProjectInfoResData[],number:number,size:number,totalElements:number } = 
                await viewProjectHistory(id,pageNo,paginationInfo.pageSize) as { content: IProjectInfoResData[] ,number:number,size:number,totalElements:number}
            setPaginationInfo({ pageIndex:number + 1,pageSize:size,total:totalElements })
            setLogLoading(false)
            const logData: any[] = []
            content.map(log => {
                const result = {
                    key: getUuid(8),
                    creator: log.creator,
                    createTime: moment(log.createTime).format('YYYY-MM-DD HH:mm:ss'),
                }
                Array.prototype.push.call(logData, { ...log ,...result })
                return logData
            })
            setCurLogList(logData)
            setTableKey(getUuid(8))
        } catch (error: any) {
            const str = getErrorStr(error)
            dispatch(requestFail(str))
            setLogLoading(false)
        }
    }

    const viewRecord = async (recordId: number) => {
        setIsModalOpen(true)
        try {
            const res: IProjectInfoResData = await getProjectInfoById(recordId)
            setRecord(res)
        } catch (error: any) {
            const str = getErrorStr(error)
            dispatch(requestFail(str))
            setLogLoading(false)
        }
    }

    const closePop = () => {
        setIsModalOpen(false)
        setViewHistoryId(null)
    }

    type EditableTableProps = Parameters<typeof Table>[0]
    type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>
    const columns: (ColumnTypes[number])[] = [
        {
            title: t('modifyTime'),
            dataIndex: 'createTime',
            key: 'createTime',
            width: 150,
            fixed: true,
        },
        {
            title: t('updator'),
            dataIndex: 'creator',
            key: 'creator',
            width: 150,
            fixed: true,
        },
        {
            title: () => <span>{t('PTN')}</span>,
            dataIndex: 'techPtn',
            key: 'techPtn',
            width: 150,
            className: 'lang_column',
            ellipsis: { showTitle: true },
            fixed: true,
        },
        {
            title: () => <span>{t('researchForms')}</span>,
            dataIndex: 'researchForms',
            key: 'researchForms',
            width: 150,
            className: 'search_col lang_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('Technology Name')}</span>,
            dataIndex: 'techName',
            key: 'technologyName',
            width: 180,
            className: 'lang_column',
            ellipsis: { showTitle: true },
            // render: wrapText
            render (value) {
                if (value && (value.includes('<div') || value.includes('<span'))) {
                    return <Popover 
                        title=""
                        content={
                            <div dangerouslySetInnerHTML={{
                                __html: (value.includes('"str":') && value.includes('"obj":')) ? JSON.parse(value).str : value
                            }}></div>
                        }
                    >
                        <div style={{ maxWidth: 180 }}
                            className='table_ellipsis' dangerouslySetInnerHTML={{
                                __html: (value.includes('"str":') && value.includes('"obj":')) ? JSON.parse(value).str : value
                            }}
                        ></div>
                    </Popover>
                } else {
                    return <div style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={value}
                    >
                        {value}
                    </div>
                }
            }
        },
        {
            title: () => <span>{t('subsidiary')}</span>,
            dataIndex: 'subsidiary',
            key: 'subsidiary',
            width: 150,
            className: 'lang_column',
            ellipsis: { showTitle: true },
            render (value) {
                if (value && (value.includes('<div') || value.includes('<span'))) {
                    return <div
                        dangerouslySetInnerHTML={{
                            __html: (value.includes('"str":') && value.includes('"obj":')) ? JSON.parse(value).str : value
                        }}
                    ></div>
                } else {
                    return <div style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={value}
                    >
                        {value}
                    </div>
                }
            }
        },
        {
            title: () => <span>{t('description')}</span>,
            dataIndex: 'description',
            width: 280,
            ellipsis: { showTitle: true },
            key: 'description',
            render (value) {
                if (value && (value.includes('<div') || value.includes('<span'))) {
                    return <Popover 
                        placement="topLeft"
                        title=""
                        content={
                            <div style={{ maxWidth: 240, maxHeight: 300, overflowY: 'auto', wordBreak: 'break-all' }}
                                dangerouslySetInnerHTML={{
                                    __html: (value.includes('"str":') && value.includes('"obj":')) ? JSON.parse(value).str : value
                                }}
                            ></div>
                        }
                    >
                        <div style={{ maxWidth: 240 }}
                            className='table_ellipsis'
                            dangerouslySetInnerHTML={{
                                __html: (value.includes('"str":') && value.includes('"obj":')) ? JSON.parse(value).str : value
                            }}
                        ></div>
                    </Popover>
                } else {
                    return <div style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={value}
                    >
                        {value}
                    </div>
                }
            }
        },
        {
            title: () => <span>{t('techLevel')}</span>,
            dataIndex: 'techLevel',
            key: 'techLevel',
            width: 100,
            className: 'search_col lang_column',
            ellipsis: { showTitle: true },
            render (value) {
                return [t('core'), t('key'), t('general'), t('normal')][value - 1]
            }
        },
        {
            title: () => <span>{t('priority')}</span>,
            dataIndex: 'priority',
            key: 'priority',
            width: 100,
            className: 'lang_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('quantitativeIndicators')}</span>,
            dataIndex: 'quantitativeIndicators',
            key: 'quantitativeIndicators',
            width: 280,
            ellipsis: { showTitle: true },
            render (value) {
                if (value && (value.includes('<div') || value.includes('<span'))) {
                    return <Popover 
                        placement="topLeft"
                        title=""
                        content={
                            <div dangerouslySetInnerHTML={{
                                __html: (value.includes('"str":') && value.includes('"obj":')) ? JSON.parse(value).str : value
                            }}></div>
                        }
                    >
                        <div style={{ maxWidth: 240 }}
                            className='table_ellipsis' 
                            dangerouslySetInnerHTML={{
                                __html: (value.includes('"str":') && value.includes('"obj":')) ? JSON.parse(value).str : value
                            }}
                        ></div>
                    </Popover>
                } else {
                    return <div style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={value}
                    >
                        {value}
                    </div>
                }
            }
        },
        {
            title: () => <span>{t('techYearQuarters')}</span>,
            dataIndex: 'techYearQuarters',
            key: 'techYearQuarters',
            width: 200,
            ellipsis: { showTitle: true },
            render (value) {
                const yq = value.map((y: any) =>{
                    return  <div key={y.year}>
                        {( y.year + '年-') + ('第' + y.quarter + '季度  ') + '状态：' + (y.status == 1 ? 'Plan' : 'On')}
                    </div> 
                })
                return yq
            }
        },
        {
            title: () => <span>{t('tmtApproval')}</span>,
            dataIndex: 'tmtapproval',
            key: 'TMTApproval',
            width: 100,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('maturity')}</span>,
            dataIndex: 'maturity',
            key: 'maturity',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('requirementSource')}</span>,
            dataIndex: 'requirementSource',
            key: 'requirementSource',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('developedBy')}</span>,
            dataIndex: 'developedBy',
            key: 'developedBy',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('buStakeholder')}</span>,
            dataIndex: 'buStakeholder',
            key: 'buStakeholder',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('toolsEquipment')}</span>,
            dataIndex: 'toolsEquipment',
            key: 'toolsEquipment',
            width: 150,
            className: 'green_column',
            ellipsis: { showTitle: true },
        },
        {
            title: () => <span>{t('resourceNeeded')}</span>,
            dataIndex: 'resourceNeeded',
            key: 'resourceNeeded',
            width: 200,
            className: 'green_column',
            ellipsis: { showTitle: true },
        }
    ]

    

    useEffect(() => {
        setWinh(document.body.clientHeight - 300)
        window.onresize = () => {
            setWinh(document.body.clientHeight - 300)
        }
        return () => {
            window.onresize = null
        }
    }, [])
    useEffect(() => {
        if (recordId) {
            viewLogList(recordId,paginationInfo.pageIndex)
            viewRecord(recordId)
        }
    }, [recordId])

    const onChangeCallBack = (page: number) =>{
        if (recordId) {
            viewLogList(recordId,page)
        }
    }

    function initDiagram() {
        const $ = go.GraphObject.make
        const myDiagram =
        $(go.Diagram,
            {
                allowCopy: false,
                'draggingTool.dragsTree': true,
                'commandHandler.deletesTree': true,
                layout:
            $(go.TreeLayout,
                { angle: 90, arrangement: go.TreeLayout.ArrangementFixedRoots }),
                'undoManager.isEnabled': true
            })

        // when the document is modified, add a "*" to the title and enable the "Save" button
        myDiagram.addDiagramListener('Modified', e => {
            console.log(e)
            // const button = document.getElementById('SaveButton')
            // if (button) button.disabled = !myDiagram.isModified
            const idx = document.title.indexOf('*')
            if (myDiagram.isModified) {
                if (idx < 0) document.title += '*'
            } else {
                if (idx >= 0) document.title = document.title.slice(0, idx)
            }
        })

        const bluegrad = $(go.Brush, 'Linear', { 0: '#C4ECFF', 1: '#70D4FF' })
        const greengrad = $(go.Brush, 'Linear', { 0: '#B1E2A5', 1: '#7AE060' })

        // each action is represented by a shape and some text
        const actionTemplate =
      $(go.Panel, 'Horizontal',
          $(go.Shape,
              { width: 12, height: 12 },
              new go.Binding('figure'),
              new go.Binding('fill')
          ),
          $(go.TextBlock,
              { font: '10pt Verdana, sans-serif' },
              new go.Binding('text')
          )
      )

        // each regular Node has body consisting of a title followed by a collapsible list of actions,
        // controlled by a PanelExpanderButton, with a TreeExpanderButton underneath the body
        myDiagram.nodeTemplate =  // the default node template
      $(go.Node, 'Vertical',
          new go.Binding('isTreeExpanded').makeTwoWay(),  // remember the expansion state for
          new go.Binding('wasTreeExpanded').makeTwoWay(), //   when the model is re-loaded
          { selectionObjectName: 'BODY' },
          // the main "BODY" consists of a RoundedRectangle surrounding nested Panels
          $(go.Panel, 'Auto',
              { name: 'BODY' },
              $(go.Shape, 'Rectangle',
                  { fill: bluegrad, stroke: null }
              ),
              $(go.Panel, 'Vertical',
                  { margin: 3 },
                  // the title
                  $(go.TextBlock,
                      {
                          stretch: go.GraphObject.Horizontal,
                          font: 'bold 12pt Verdana, sans-serif'
                      },
                      new go.Binding('text', 'question')
                  ),
                  // the optional list of actions
                  $(go.Panel, 'Vertical',
                      { stretch: go.GraphObject.Horizontal, visible: false },  // not visible unless there is more than one action
                      new go.Binding('visible', 'actions', acts => (Array.isArray(acts) && acts.length > 0)),
                      // headered by a label and a PanelExpanderButton inside a Table
                      $(go.Panel, 'Table',
                          { stretch: go.GraphObject.Horizontal },
                          $('PanelExpanderButton', 'COLLAPSIBLE',  // name of the object to make visible or invisible
                              { column: 1, alignment: go.Spot.Right }
                          )
                      ), // end Table panel
                      // with the list data bound in the Vertical Panel
                      $(go.Panel, 'Vertical',
                          {
                              name: 'COLLAPSIBLE',  // identify to the PanelExpanderButton
                              padding: 2,
                              stretch: go.GraphObject.Horizontal,  // take up whole available width
                              background: 'white',  // to distinguish from the node's body
                              defaultAlignment: go.Spot.Left,  // thus no need to specify alignment on each element
                              itemTemplate: actionTemplate  // the Panel created for each item in Panel.itemArray
                          },
                          new go.Binding('itemArray', 'actions')  // bind Panel.itemArray to nodedata.actions
                      )  // end action list Vertical Panel
                  )  // end optional Vertical Panel
              )  // end outer Vertical Panel
          ),  // end "BODY"  Auto Panel
          $(go.Panel,  // this is underneath the "BODY"
              { height: 17 },  // always this height, even if the TreeExpanderButton is not visible
              $('TreeExpanderButton')
          )
      )

        // define a second kind of Node:
        myDiagram.nodeTemplateMap.add('Terminal',
            $(go.Node, 'Spot',
                $(go.Shape, 'Circle',
                    { width: 55, height: 55, fill: greengrad, stroke: null }
                ),
                $(go.TextBlock,
                    { font: '10pt Verdana, sans-serif' },
                    new go.Binding('text')
                )
            )
        )

        myDiagram.linkTemplate =
      $(go.Link, go.Link.Orthogonal,
          { deletable: false, corner: 10 },
          $(go.Shape,
              { strokeWidth: 2 }
          ),
          $(go.TextBlock, go.Link.OrientUpright,
              {
                  background: 'white',
                  visible: false,  // unless the binding sets it to true for a non-empty string
                  segmentIndex: -2,
                  segmentOrientation: go.Link.None
              },
              new go.Binding('text', 'answer'),
              // hide empty string;
              // if the "answer" property is undefined, visible is false due to above default setting
              new go.Binding('visible', 'answer', a => a ? true : false)
          )
      )

        // create the Model with the above data, and assign to the Diagram
        myDiagram.model = new go.GraphLinksModel(
            {
                copiesArrays: true,
                copiesArrayObjects: true,
                nodeDataArray: nodeDataArray,
                linkDataArray: linkDataArray,
                linkKeyProperty :'test'
                // mergeLinkDataArray:linkDataArray
            })
      
        return myDiagram
    }

    useEffect(() => {
        if (record) {
            console.log(record)
            const nodeDataArray: React.SetStateAction<{ key: number; question: string; actions: { text: string; figure: string; fill: string }[] }[]> = []
            const linkDataArray: React.SetStateAction<{ from: number; to: number }[]> = []
            appendData(record,nodeDataArray)
            if(record.parent){
                appendParent(record,nodeDataArray,linkDataArray)
            }
            if(record.children && record.children.length > 0){
                appendSubChildren(record,nodeDataArray,linkDataArray)
            }
            setNodeDataArray(nodeDataArray)
            setLinkDataArray(linkDataArray)
        }
    }, [record])

    function appendData(record:IProjectInfoResData,nodeDataArray: { key: any; question: string; actions: { text: string; figure: string; fill: string }[] }[]){
        let techName = ''
        if (record.techName.includes('"str":') && record.techName.includes('"obj":')) {
            techName = JSON.parse(record.techName).str
            techName = techName.replace(/<\/?.+?\/?>/g,'')
        } else {
            techName = record.techName
        }
        techName = techName.replace(/\n/g, '')
        const actions = []
        if(record.techPtn){
            actions.push({ text: t('PTN') + ':' + record.techPtn, figure: 'ElectricalHazard', fill: 'rgba(0,0,255,0.5)' })
        }
        if(record.subsidiary){
            actions.push({ text: t('subsidiary') + ':' + record.subsidiary, figure: 'ElectricalHazard', fill: 'tomato' })
        }
        if(record.tmtapproval){
            actions.push( { text: t('tmtApproval') + ':' + record.tmtapproval, figure: 'FireHazard', fill: 'rgba(255,0,0,0.5)' })
        }
        if(record.maturity){
            actions.push({ text: t('maturity') + ':' + record.maturity, figure: 'IrritationHazard', fill: 'rgba(0,255,0,0.5)' })
        }
        if(record.priority){
            actions.push({ text: t('priority') + ':' + record.priority, figure: 'IrritationHazard', fill: 'yellow' })
        }
        if(record.techLevel){
            actions.push({ text: t('techLevel') + ':' +  [t('core'), t('key'), t('general'), t('normal')][record.techLevel - 1], figure: 'IrritationHazard', fill: 'brown' })
        }
        nodeDataArray.push(
            {
                key: record.id, question: techName,
                actions: actions
            },
        )
    }

    function appendParent(record: IProjectInfoResData,nodeDataArray: { key: any; question: string; actions: { text: string; figure: string; fill: string }[] }[],linkDataArray: { from: any; to: any }[]) {
        const parentRecord = record.parent
        appendData(parentRecord,nodeDataArray)
        linkDataArray.push({ from:parentRecord.id,to:record.id })
        if(parentRecord.parent){
            appendParent(parentRecord,nodeDataArray,linkDataArray)
        }
    }

    function appendSubChildren(record: IProjectInfoResData,nodeDataArray: { key: any; question: string; actions: { text: string; figure: string; fill: string }[] }[],linkDataArray: { from: any; to: any }[]) {
        record.children.forEach(res =>{
            appendData(res,nodeDataArray)
            linkDataArray.push({ from:record.id,to:res.id }) 
            if(res.children && res.children.length > 0){
                appendSubChildren(res,nodeDataArray,linkDataArray)
            }
        })
    }

    function handleModelChange(changes: any) {
        console.log(changes)
    }
    return (
        <Modal title={t('Modify History')} visible={isModalOpen} 
            onOk={closePop}
            onCancel={closePop}
            width='90%'
            centered
            footer={[
                <Button key="submit" type="primary" onClick={closePop}>{t('close')}</Button>
            ]}
        >
            <div>{t('projectFamilyTreeGraph')}</div>
            <ReactDiagram
                initDiagram={initDiagram}
                divClassName='diagram-component'
                nodeDataArray={nodeDataArray}
                linkDataArray={linkDataArray}
                onModelChange={handleModelChange}
            />
            <Table key={tableKey} scroll={{ x: '100%', y: winH }} bordered
                dataSource={curLogList} 
                columns={columns}
                loading={logLoading}
                pagination={{
                    current: paginationInfo.pageIndex,
                    pageSize: paginationInfo.pageSize,
                    total: paginationInfo.total,
                    onChange: onChangeCallBack,
                    showTotal: (total) => 
                        `${t('total')} ${total} ${t('items')}`
                }}
            />
        </Modal>
    )
}